import { useState } from "react";
import {
  useFloating,
  useClick,
  useInteractions,
  autoUpdate,
  shift,
  useRole,
  useDismiss,
  FloatingPortal,
  offset,
  flip,
  FloatingFocusManager,
} from "@floating-ui/react";
import { useNavigate } from "react-router-dom";

// components
import { Icon } from "@trussworks/react-uswds";
import { RoundedButton } from "components/LibraryComponents/Components";
import { ExportResultsDialog } from "components/Dialogs";

// constants
import { ExportList } from "constants/filters";

// types
import { BusinessItem } from "types/results-panel.types";

// generic
import { Generic } from "types/filters-store.types";

// styles
import styles from "./ExportBy.module.scss";

type ExportByProps = {
  data: BusinessItem[] | undefined;
  isLoading?: boolean;
};

export default function ExportBy({ data, isLoading }: ExportByProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isOpenExportDialog, setIsOpenExportDialog] = useState<boolean>(false);

  const navigate = useNavigate();

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    placement: "bottom-end",
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift()],
  });

  const click = useClick(context);
  const role = useRole(context);
  const dismiss = useDismiss(context, { bubbles: true });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    role,
    dismiss,
  ]);

  const handleChange = (item: Generic) => {
    if (item.value === "export-email-addresses") {
      navigate("/emails-export");
    } else {
      setIsOpenExportDialog(true);
    }
  };

  return (
    <>
      <div className={styles["export-by-root"]}>
        <RoundedButton
          variant="ghost"
          className={isOpen ? styles["active"] : ""}
          ref={refs.setReference}
          disabled={isLoading}
          {...getReferenceProps()}
        >
          <Icon.SaveAlt aria-label="Export results" />
          Export
        </RoundedButton>

        <FloatingPortal id="export-by">
          <FloatingFocusManager context={context} modal={false} guards={false}>
            {isOpen && (
              <div
                ref={refs.setFloating}
                style={{ ...floatingStyles, outline: "none" }}
                {...getFloatingProps()}
                className={styles["content"]}
              >
                {/* this div is required to make the focus trap work */}
                <div tabIndex={0} style={{ outline: "none" }} />

                <ul className={styles["list"]}>
                  {ExportList.map((item, index) => (
                    <li
                      key={index}
                      className={styles["list-item"]}
                      onClick={() => handleChange(item)}
                    >
                      {item.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </FloatingFocusManager>
        </FloatingPortal>
      </div>

      {isOpenExportDialog && (
        <ExportResultsDialog
          open={isOpenExportDialog}
          setOpen={setIsOpenExportDialog}
          data={data}
        />
      )}
    </>
  );
}
